/**
 * Script-tag entry point (IIFE bundle, globalName DeuteroInterview):
 *
 *   <script async src="https://api.example.com/widget/v1/deutero-widget.js"
 *           data-publishable-key="dtro_pk_…"
 *           data-survey-id="…"
 *           data-mode="chat"
 *           data-metadata='{"plan":"pro"}'
 *           data-language="es"></script>
 *
 * data-language-picker="false" hides the welcome splash's language picker;
 * data-study-theme="false" keeps the widget's own look instead of the
 * study's brand theme.
 *
 * Auto-initializes from data-* attributes when a publishable key (or signed
 * token) is present; otherwise the page calls DeuteroInterview.init({...}).
 * apiBaseUrl defaults to the origin the script was loaded from.
 */
import { init } from "./index";
import type { DeuteroInterviewConfig, WidgetMode } from "./types";

export { init };

function autoInit(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;
  const data = script.dataset;
  if (!data.publishableKey && !data.signedToken) return;

  let metadata: DeuteroInterviewConfig["metadata"];
  if (data.metadata) {
    try {
      metadata = JSON.parse(data.metadata);
    } catch {
      console.error("[deutero] data-metadata is not valid JSON — ignored");
    }
  }
  let apiBaseUrl = data.apiBaseUrl;
  if (!apiBaseUrl && script.src) {
    apiBaseUrl = new URL(script.src).origin;
  }

  const config: DeuteroInterviewConfig = {
    apiBaseUrl,
    publishableKey: data.publishableKey,
    sessionBootstrapToken: data.signedToken,
    surveyId: data.surveyId,
    metadata,
    mode: (data.mode as WidgetMode) || "chat",
    position: data.position === "bottom-left" ? "bottom-left" : "bottom-right",
    openOnLoad: data.openOnLoad === "true",
    title: data.title,
    language: data.language,
    languagePicker: data.languagePicker !== "false",
    useStudyTheme: data.studyTheme !== "false",
  };

  const mount = () => {
    try {
      init(config);
    } catch (err) {
      console.error("[deutero] widget init failed", err);
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount, { once: true });
  } else {
    mount();
  }
}

autoInit();
